import { defaultPaginatedResponse, mapPaginatedResponse } from '@/mappers/common.mapper';
import {
  mapToolCreateResponse,
  mapToolDetailResponse,
  mapToolResponse,
} from '@/mappers/tool.mapper';

import apiClient from '@/lib/api-client';
import { raiseAxiosErrorOrDefault } from '@/lib/error';

import type { PaginatedResponse, PaginationParams } from '@/types/common.type';
import type { Tool, ToolCreateRequest, ToolUpdateRequest } from '@/types/tool.type';

type KeyValuePair = { key?: string; value?: string };

type MCPConfigInput = {
  url?: string;
  timeout?: number;
  client_session_timeout_seconds?: number;
  headers?: KeyValuePair[];
  query_params?: KeyValuePair[];
};

export const buildMCPConfig = (values: MCPConfigInput) => ({
  transport: 'http',
  url: values.url ?? '',
  ...(values.timeout !== undefined && { timeout: values.timeout }),
  ...(values.client_session_timeout_seconds !== undefined && {
    client_session_timeout_seconds: values.client_session_timeout_seconds,
  }),
  ...(values.headers &&
    values.headers.length > 0 && {
      headers: Object.fromEntries(
        values.headers.filter(h => h.key).map(h => [h.key!, h.value ?? ''])
      ),
    }),
  ...(values.query_params &&
    values.query_params.length > 0 && {
      query_params: Object.fromEntries(
        values.query_params.filter(p => p.key).map(p => [p.key!, p.value ?? ''])
      ),
    }),
});

export const fetchAllTools = async (
  params?: PaginationParams
): Promise<PaginatedResponse<Tool>> => {
  try {
    return await apiClient.get<PaginatedResponse<Tool>>(
      '/tools',
      mapPaginatedResponse(mapToolResponse),
      {
        params: {
          page: params?.page || 1,
          page_size: params?.page_size || 20,
          ...(params?.query && { query: params.query }),
        },
      }
    );
  } catch {
    return defaultPaginatedResponse<Tool>();
  }
};

export const fetchToolById = async (toolId: string): Promise<Tool> => {
  try {
    return await apiClient.get<Tool>(`/tool/${toolId}`, mapToolDetailResponse);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to fetch tool');
  }
};

export const createTool = async (payload: ToolCreateRequest): Promise<Tool> => {
  try {
    return await apiClient.post<Tool>('/tools', payload, mapToolCreateResponse);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to create tool');
  }
};

export const updateTool = async (toolId: string, payload: ToolUpdateRequest): Promise<Tool> => {
  try {
    return await apiClient.put<Tool>(`/tool/${toolId}`, payload, mapToolDetailResponse);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to update tool');
  }
};

export const deleteTool = async (toolId: string): Promise<void> => {
  try {
    await apiClient.delete(`/tool/${toolId}`);
  } catch (e) {
    raiseAxiosErrorOrDefault(e, 'Failed to delete tool');
  }
};
